import type {
  CreatePortalServiceRequestInput,
  ExpertReport,
  PortalDriveDocument,
  PortalDriveQuery,
  PortalDriveResponse,
  PortalDriveUploadInput,
  PortalScope,
  PortalServiceRequestItem,
  PortalServiceRequestListResponse,
  Session,
} from "./types";
import { demoAlcanceDoRepresentante, demoPortalEhRepresentante } from "./demo/portal-representante";
import { recusaDoPedidoDoPortal, resumoDoPedido, ROTULO_DO_TIPO } from "./pedido-do-portal";

/**
 * TUT-080 fase 2c — o portal externo no modo demo.
 *
 * Segue o precedente do `platform-demo.ts`: a superfície nova tem o próprio
 * arquivo de dados sintéticos, em vez de engordar o `demo-data.ts`. O harness
 * (`?__tutelae_portal=1`, só em `VITE_API_MODE=demo`) segue o precedente do
 * `__tutelae_maintenance`: é o que permite ao e2e — que roda sem API — abrir a
 * tela do portal e passar axe/reflow nela, sem existir usuário externo real.
 */

export const demoPortalSession: Session = {
  user: {
    id: "portal-demo-user",
    name: "Dra. Marina Externa",
    email: "marina.externa@advocacia-sintetica.example",
    initials: "ME",
    role: "Externo",
    roleKey: "EXTERNAL",
  },
  tenant: {
    id: "portal-demo-tenant",
    name: "Escritório Demonstrativo",
    slug: "demonstrativo",
  },
  csrfToken: "portal-demo-csrf",
  semGrupo: false,
  permissions: [],
};

/**
 * TUT-472 — as datas que a tela trata como FUTURAS (próxima perícia, prazos
 * em aberto) são relativas a hoje. Fixas, elas venceram: "Próxima perícia
 * 01/09/2026" seguia com "Confirmar presença" ativo em outubro.
 */
function diasAdiante(dias: number, horaUtc: number): string {
  const data = new Date();
  data.setUTCHours(horaUtc, 0, 0, 0);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString();
}

export const demoPortalScope: PortalScope = {
  profession: "COUNSEL",
  personName: "Dra. Marina Externa",
  registration: "OAB/PR 99.999",
  specialty: null,
  validFrom: "2026-06-01",
  processes: [
    {
      id: "portal-p1",
      caseNumber: "0001234-55.2026.5.09.0001",
      claimantName: "Reclamante Sintética",
      defendantName: "Metalúrgica Sintética Ltda.",
      subject: "Insalubridade",
      court: "1ª VT de Curitiba",
      jurisdiction: "TRT-9",
      status: "ACTIVE",
      nextEventLabel: "Perícia agendada",
      nextEventAt: diasAdiante(9, 13),
      filedAt: "2026-03-10",
      // TUT-570 — a Dra. Marina é pessoa: ela vê o processo em que está designada.
      designados: [{ counselId: "demo-marina", name: "Dra. Marina Externa", desde: "2026-08-12T14:22:00.000Z", origem: "solicitacao" }],
    },
    {
      id: "portal-p2",
      caseNumber: "0005678-90.2026.5.09.0003",
      claimantName: "Autor Sintético",
      defendantName: "Transportes Sintéticos S.A.",
      subject: "Periculosidade",
      court: "3ª VT de Curitiba",
      jurisdiction: "TRT-9",
      status: "ACTIVE",
      nextEventLabel: null,
      nextEventAt: null,
      filedAt: "2026-05-22",
      designados: [{ counselId: "demo-marina", name: "Dra. Marina Externa", desde: "2026-09-03T09:30:00.000Z", origem: "mh" }],
    },
  ],
  expertises: [
    {
      id: "portal-e1",
      processId: "portal-p1",
      caseNumber: "0001234-55.2026.5.09.0001",
      status: "SCHEDULED",
      statusLabel: "Perícia agendada",
      specialty: "Engenharia de Segurança",
      expertName: "Perito Sintético",
      scheduledAt: diasAdiante(9, 13),
      location: "Planta industrial — Curitiba/PR",
      timeZone: null,
      subject: "Insalubridade",
      presenceConfirmedAt: null,
      // TUT-507 — a advogada não tem papel na perícia; o assistente, sim
      // (`demoPortalScopeAtual`).
      papel: null,
      convocacao: null,
    },
  ],
  reports: [
    {
      id: "portal-r1",
      expertiseId: "portal-e1",
      caseNumber: "0001234-55.2026.5.09.0001",
      specialty: "Engenharia de Segurança",
      sequence: 1,
      status: "FILED",
      filedAt: "2026-07-10T18:00:00.000Z",
      // TUT-490d2 — a advogada ainda não aceitou: é o botão que a e2e aperta.
      protocolReceivedAt: null,
      mine: false,
    },
  ],
  briefs: [
    {
      id: "portal-b1",
      processId: "portal-p1",
      caseNumber: "0001234-55.2026.5.09.0001",
      type: "IMPUGNATION",
      ordinal: 1,
      status: "DELIVERED",
      subject: "Impugnação ao laudo",
      dueAt: "2026-07-01T12:00:00.000Z",
      deliveredAt: "2026-06-28T14:30:00.000Z",
    },
  ],
  // TUT-533 — os contadores saem do estado do demo (`demo/visao-geral-do-portal.ts`),
  // como o servidor os soma; aqui nascem zerados.
  contadores: {
    aguardandoAceite: 0,
    solicitacoesAtivas: 0,
    pendenteResposta: 0,
    paraProtocolo: 0,
    pendenteEnvioDoProtocolo: 0,
  },
  novosAndamentos: [
    {
      id: "portal-a1",
      processId: "portal-p1",
      caseNumber: "0001234-55.2026.5.09.0001",
      kind: "EXPERTISE_CREATED",
      title: "Perícia criada: Engenharia de Segurança",
      occurredAt: diasAdiante(-2, 14),
    },
    {
      id: "portal-a2",
      processId: "portal-p2",
      caseNumber: "0005678-90.2026.5.09.0003",
      kind: "DEADLINE_CREATED",
      title: "Prazo criado: Quesitos",
      occurredAt: diasAdiante(-4, 11),
    },
  ],
};

let demoPortalDocuments: PortalDriveDocument[] = [
  {
    id: "portal-drive-1",
    title: "Comprovante de endereço",
    category: { key: "other", label: "Outro" },
    confidentiality: { key: "restricted", label: "Restrito" },
    versionCount: 1,
    latestVersion: {
      id: "portal-drive-version-1",
      versionNumber: 1,
      originalName: "comprovante-endereco.pdf",
      mimeType: "application/pdf",
      sizeBytes: 824_300,
      status: { key: "available", label: "Disponível", tone: "success" },
      rejectionReason: null,
      author: "Dra. Marina Externa",
      createdAt: "2026-08-06T14:30:00.000Z",
    },
    versions: [],
    createdAt: "2026-08-06T14:30:00.000Z",
    updatedAt: "2026-08-06T14:30:00.000Z",
  },
];
demoPortalDocuments[0]!.versions = [demoPortalDocuments[0]!.latestVersion];

export function demoPortalDrive(
  query: PortalDriveQuery = {},
): PortalDriveResponse {
  const search = query.search?.trim().toLocaleLowerCase("pt-BR");
  const items = demoPortalDocuments.filter((document) => {
    if (
      query.status &&
      document.latestVersion.status.key !==
        query.status.toLocaleLowerCase("en-US")
    ) {
      return false;
    }
    if (!search) return true;
    return (
      document.title.toLocaleLowerCase("pt-BR").includes(search) ||
      document.latestVersion.originalName
        .toLocaleLowerCase("pt-BR")
        .includes(search)
    );
  });
  const usedBytes = demoPortalDocuments.reduce(
    (total, document) =>
      total +
      (document.latestVersion.status.key === "rejected"
        ? 0
        : document.latestVersion.sizeBytes),
    0,
  );
  const quotaBytes = 1_000_000_000;
  return {
    items: structuredClone(items),
    usage: {
      usedBytes,
      quotaBytes,
      freeBytes: quotaBytes - usedBytes,
      usagePercent: (usedBytes / quotaBytes) * 100,
      warning: false,
      blocked: false,
    },
  };
}

export async function demoUploadPortalDrive(
  input: PortalDriveUploadInput,
): Promise<PortalDriveDocument> {
  const now = new Date().toISOString();
  const id = `portal-drive-${crypto.randomUUID()}`;
  const version: PortalDriveDocument["latestVersion"] = {
    id: `portal-drive-version-${crypto.randomUUID()}`,
    versionNumber: 1,
    originalName: input.file.name,
    mimeType: input.file.type,
    sizeBytes: input.file.size,
    status: { key: "available", label: "Disponível", tone: "success" },
    rejectionReason: null,
    author: demoPortalScope.personName,
    createdAt: now,
  };
  const document: PortalDriveDocument = {
    id,
    title: input.title,
    category: { key: "other", label: "Outro" },
    confidentiality: { key: "restricted", label: "Restrito" },
    versionCount: 1,
    latestVersion: version,
    versions: [version],
    createdAt: now,
    updatedAt: now,
  };
  demoPortalDocuments = [document, ...demoPortalDocuments];
  return structuredClone(document);
}

/**
 * TUT-294b — "graduar" o documento do drive para um processo (o comprovante do
 * protocolo e o "Enviar ao processo"): no servidor ele ganha o vínculo e sai
 * do drive; aqui sai da lista e volta para quem o leva ao processo.
 */
export function demoTirarDoDrive(documentId: string): PortalDriveDocument | null {
  const documento = demoPortalDocuments.find((candidato) => candidato.id === documentId);
  if (!documento) return null;
  demoPortalDocuments = demoPortalDocuments.filter((candidato) => candidato.id !== documentId);
  return structuredClone(documento);
}

/** TUT-294b — os ids do drive de agora (as pastas só guardam documento do drive). */
export function demoIdsDoDrive(): string[] {
  return demoPortalDocuments.map((documento) => documento.id);
}

export function demoPortalDriveAccessLink() {
  return {
    url: "data:text/plain;charset=utf-8,Visualiza%C3%A7%C3%A3o%20sint%C3%A9tica%20do%20drive%20Tutelae",
    expiresInSeconds: 300,
  };
}

// ---------------------------------------------------------------------------
// TUT-294a — as solicitações no portal, no modo demo. Espelho de
// `presentPortalServiceRequest` (`apps/api/src/http/presenters/atendimento.ts`): a
// situação de quem PEDIU (TUT-533: Solicitado, Aceita, Recusada, Para protocolo ou Pendente).
// ---------------------------------------------------------------------------

let demoServiceRequests: PortalServiceRequestItem[] = [
  {
    id: "portal-sr-1",
    kind: "EXPERTISE",
    kindLabel: ROTULO_DO_TIPO.EXPERTISE,
    summary: "Agendar perícia de insalubridade",
    details: null,
    status: "PENDING",
    situacao: "SOLICITADO",
    statusLabel: "Solicitado",
    refusalReason: null,
    caseNumber: demoPortalScope.processes[0]?.caseNumber ?? null,
    // TUT-525 — o pedido antigo, de antes da ficha completa, não tem prazo.
    dueDate: null,
    createdAt: "2026-09-10T13:00:00.000Z",
    decidedAt: null,
  },
  {
    id: "portal-sr-2",
    kind: "QUESTIONS",
    kindLabel: ROTULO_DO_TIPO.QUESTIONS,
    summary: "Quesitos complementares",
    details: null,
    status: "REFUSED",
    situacao: "RECUSADA",
    statusLabel: "Recusada",
    refusalReason: "Já cobertos pelo laudo anterior.",
    caseNumber: demoPortalScope.processes[1]?.caseNumber ?? null,
    dueDate: "2026-08-15",
    createdAt: "2026-08-01T13:00:00.000Z",
    decidedAt: "2026-08-03T09:00:00.000Z",
  },
];

export function demoPortalSolicitacoes(): PortalServiceRequestListResponse {
  return { items: structuredClone(demoServiceRequests) };
}

/**
 * TUT-525 — a ficha completa no demo, pela MESMA régua da tela e do servidor
 * (`lib/pedido-do-portal.ts`): a falta é recusada com a frase da tela, o
 * processo vem do alcance, o resumo é derivado e os anexos saem do drive (no
 * servidor eles caem no processo; aqui o demo não os relista lá).
 */
export function demoCreatePortalSolicitacao(
  input: CreatePortalServiceRequestInput,
): PortalServiceRequestItem {
  return demoRegistrarSolicitacaoDoPortal(input).item;
}

/**
 * TUT-531 — o mesmo registro, devolvendo também os anexos que saíram do drive:
 * a ponte do portal com o escritório (`lib/api/ponte-do-portal.ts`) os lança
 * nos documentos do processo, com a advogada como quem incluiu.
 */
export function demoRegistrarSolicitacaoDoPortal(input: CreatePortalServiceRequestInput): {
  item: PortalServiceRequestItem;
  anexos: PortalDriveDocument[];
} {
  const recusa = recusaDoPedidoDoPortal(input);
  if (recusa) throw new Error(recusa);
  const processo = demoPortalScope.processes.find((p) => p.id === input.processId);
  if (!processo) throw new Error("Processo não encontrado.");
  const doDrive = new Set(demoIdsDoDrive());
  if (input.documentIds.some((id) => !doDrive.has(id))) {
    throw new Error("Documento não encontrado no seu drive.");
  }
  const anexos = input.documentIds.map((id) => demoTirarDoDrive(id)!);
  const created: PortalServiceRequestItem = {
    id: `portal-sr-${crypto.randomUUID()}`,
    kind: input.kind,
    kindLabel: ROTULO_DO_TIPO[input.kind],
    summary: input.summary?.trim() || resumoDoPedido(input.kind, processo.caseNumber),
    details: input.details?.trim() || null,
    status: "PENDING",
    situacao: "SOLICITADO",
    statusLabel: "Solicitado",
    refusalReason: null,
    caseNumber: processo.caseNumber,
    dueDate: input.dueDate,
    createdAt: new Date().toISOString(),
    decidedAt: null,
  };
  demoServiceRequests = [created, ...demoServiceRequests];
  return { item: structuredClone(created), anexos };
}

// ---------------------------------------------------------------------------
// TUT-295 — os dois gestos do assistente no portal, no modo demo.
// ---------------------------------------------------------------------------

/**
 * O harness do portal tem três pessoas: `?__tutelae_portal=1` é a advogada,
 * `?__tutelae_portal=perito` é o assistente — sem ele, os gestos do assistente
 * só eram provados na tela da advogada, que o servidor recusaria (403) — e
 * `?__tutelae_portal=representante` é o representante local (TUT-507).
 */
export function demoPortalEhPerito(): boolean {
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("__tutelae_portal") === "perito"
  );
}

export function demoPortalScopeAtual(): PortalScope {
  if (demoPortalEhRepresentante()) {
    // TUT-507 — o representante local: só as perícias em que é o
    // representante (o alcance do servidor), sem laudos nem peças.
    return {
      ...demoPortalScope,
      profession: "EXPERT",
      personName: "Eng.ª Renata Representante",
      registration: "CREA/SP 50.507",
      specialty: "Engenharia de Segurança",
      ...demoAlcanceDoRepresentante(),
      reports: [],
      briefs: [],
    };
  }
  if (!demoPortalEhPerito()) return demoPortalScope;
  return {
    ...demoPortalScope,
    profession: "EXPERT",
    personName: "Eng. Rafael Assistente",
    registration: "CREA/PR 12.345",
    specialty: "Engenharia de Segurança",
    // TUT-507 — nestas perícias ele é o assistente técnico (TUT-309).
    expertises: demoPortalScope.expertises.map((e) => ({ ...e, papel: "ASSISTENTE" as const })),
  };
}

function exigirPeritoNoDemo() {
  // Mesma fronteira do servidor (`exigirPerito`): advogado não confirma
  // presença nem envia relatório.
  if (!demoPortalEhPerito()) throw new Error("Só o assistente faz este gesto.");
}

export function demoPortalConfirmarPresenca(
  expertiseId: string,
): { id: string; presenceConfirmedAt: string | null } {
  exigirPeritoNoDemo();
  const expertise = demoPortalScope.expertises.find((e) => e.id === expertiseId);
  if (expertise && !expertise.presenceConfirmedAt) {
    expertise.presenceConfirmedAt = new Date().toISOString();
  }
  return {
    id: expertiseId,
    presenceConfirmedAt: expertise?.presenceConfirmedAt ?? null,
  };
}

/**
 * TUT-490d2 — a advogada aceita o protocolo do laudo. Mesma fronteira do
 * servidor: o assistente não aceita protocolo, e só o laudo protocolado.
 * Idempotente, como a presença.
 */
export function demoPortalConfirmarRecebimentoDoProtocolo(
  reportId: string,
): { id: string; protocolReceivedAt: string } {
  if (demoPortalEhPerito()) throw new Error("Aceitar o protocolo é um gesto do advogado.");
  const laudo = demoPortalScope.reports.find((r) => r.id === reportId && r.status === "FILED");
  if (!laudo) throw new Error("Laudo protocolado não encontrado.");
  laudo.protocolReceivedAt ??= new Date().toISOString();
  return { id: reportId, protocolReceivedAt: laudo.protocolReceivedAt };
}

export function demoPortalEnviarRelatorio(
  expertiseId: string,
  _documentId: string,
): ExpertReport {
  exigirPeritoNoDemo();
  const expertise = demoPortalScope.expertises.find((e) => e.id === expertiseId);
  if (expertise) {
    expertise.status = "COMPLETED";
    expertise.statusLabel = "Perícia concluída";
  }
  const now = new Date().toISOString();
  return {
    id: `portal-report-${crypto.randomUUID()}`,
    expertiseId,
    specialty: expertise?.specialty ?? "",
    sequence: 1,
    status: { key: "drafting", label: "Em elaboração", tone: "warning" },
    assignedUserId: null,
    assignedUserName: null,
    assignedAt: null,
    assignedAtEstimated: false,
    assignedReviewerUserId: null,
    assignedReviewerUserName: null,
    reviewerUserName: null,
    filedAt: null,
    protocolReceivedAt: null,
    dueAt: null,
    reopenedFromId: null,
    createdAt: now,
    updatedAt: now,
    allowedTransitions: [],
    reopenable: false,
    reopenableByActor: false,
    transitions: [],
    reviewerAssignments: [],
  };
}
